"use client"

import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"

import { cn } from "@/lib/utils"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Search } from "@/icons/Icon"

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    // `flex-1 min-h-0` (not `h-full`) — inside `CommandDialog`'s
    // viewport-capped flex column, this is the item that has to shrink to
    // whatever room the cap leaves; `h-full`/percentage sizing has no
    // definite parent height to resolve against here (`DialogContent` only
    // sets a `max-height`), while flex's own grow/shrink algorithm does the
    // right thing regardless.
    className={cn(
      "flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-lg bg-popover text-popover-foreground",
      className,
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

type CommandDialogProps = React.ComponentPropsWithoutRef<typeof Dialog> & {
  contentClassName?: string
  /** Accessible name/description for the dialog — rendered visually hidden
   * (Radix requires a `DialogTitle` for `aria-labelledby`; the palette's own
   * search input is the visible label a sighted user reads instead). */
  title?: string
  description?: string
  /** Forwarded to the inner `Command` — replaces cmdk's default fuzzy scorer. */
  filter?: React.ComponentPropsWithoutRef<typeof CommandPrimitive>["filter"]
}

const CommandDialog = ({
  children,
  contentClassName,
  title = "Command palette",
  description = "Search for a page or action",
  filter,
  ...props
}: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent
        showCloseButton={false}
        // `flex flex-col` (overriding the base `grid`) + a height CAP tied to
        // the `top-[18%]` offset is what makes this responsive to viewport
        // height rather than just a bigger fixed number: `calc(82vh-24px)`
        // is "100vh, minus the 18% we're offset from the top, minus a 24px
        // bottom gutter" — the panel's bottom edge can never cross the
        // viewport edge at any window height, short or tall. `min(560px,…)`
        // keeps it from growing huge on very tall monitors. `Command`
        // (`flex-1 min-h-0`) and `CommandList` (`flex-1 min-h-0
        // overflow-y-auto`) are what actually absorb that cap — the input
        // row and this footer stay their natural size and only the list
        // area shrinks/scrolls, so the footer is never the thing pushed
        // off-screen.
        className={cn(
          // `calc()` requires whitespace around `-`/`+` (CSS syntax, not a
          // Tailwind quirk) — `calc(82vh_-_24px)` is the underscore-escaped
          // form of `calc(82vh - 24px)`; writing it as `calc(82vh-24px)`
          // (no spaces) is invalid CSS, silently drops the whole
          // `max-height` declaration, and would have reintroduced this
          // exact bug while looking fixed in the diff.
          "top-[18%] flex max-h-[min(560px,calc(82vh_-_24px))] translate-y-0 flex-col overflow-hidden p-0 shadow-2xl sm:max-w-xl",
          contentClassName,
        )}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {/* No forced [cmdk-input]/[cmdk-item] height or [cmdk-item] svg size
            here (the shadcn reference sizes those up) — this app's rows run
            noticeably smaller than that default scale (see CommandInput's
            and CommandItem's own sizing below), and a CSS descendant-selector
            override on the ancestor always wins over an inner element's own
            size prop, so leaving those in silently re-inflated every icon
            back to 20px regardless of what CommandRowIcon/Search asked for. */}
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:px-2" filter={filter}>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  // `cmdk-input-wrapper` is a bare DOM attribute, not a React prop — cmdk's
  // own stylesheet and the `[cmdk-input-wrapper]` selectors in `CommandDialog`
  // above target it. React 19 passes lowercase unknown attributes straight to
  // the DOM, and this repo's ESLint registers only `react-hooks` /
  // `react-refresh` (no `eslint-plugin-react`), so nothing flags it.
  <div className="flex h-10 items-center gap-2 border-b border-border px-3" cmdk-input-wrapper="">
    <Search size={14} className="shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        // `focus-visible:outline-none` (a class, not the bare `outline-none`
        // utility) is load-bearing: global.css's `input:focus-visible {
        // outline: 2px solid var(--brand-500) }` targets the tag itself, and
        // a class selector's specificity is what beats a tag selector here —
        // the plain `outline-none` utility (no pseudo-class) never engages
        // that state at all, so the orange ring rendered anyway on focus.
        "flex h-10 w-full bg-transparent py-2 text-[12.5px] text-foreground outline-none placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
))

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    // `flex-1 min-h-0` (not a fixed `max-h-[…px]`) — this is the one region
    // that's allowed to shrink/scroll inside `CommandDialog`'s
    // viewport-capped flex column, so the input row and the footer hint row
    // (both fixed-size flex items alongside this one) always stay visible
    // regardless of window height; on a short window this scrolls internally
    // well before the dialog itself could run off the bottom of the screen.
    // `min-h-0` overrides flexbox's default `min-height: auto`, which would
    // otherwise refuse to shrink this below its content's height and defeat
    // the cap entirely.
    className={cn("min-h-0 flex-1 overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-sm text-muted-foreground"
    {...props}
  />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
      className,
    )}
    {...props}
  />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 h-px bg-border", className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    // Brand-tinted selection (keyboard nav AND mouse hover both drive
    // `aria-selected` — cmdk doesn't distinguish them), not the generic
    // `bg-accent` gray — same token pair `Badge`'s `brand` variant uses.
    className={cn(
      // 13px, not the shadcn-default text-sm (14px) — matches the sidebar's
      // (`SidebarItem`) and `/api-keys`' row-label scale rather than reading
      // a size larger than every other list in this app.
      "relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-[13px] outline-none aria-selected:bg-[color:var(--brand-50)] aria-selected:text-[color:var(--brand-700)] data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
      className,
    )}
    {...props}
  />
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "ml-auto rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium tracking-widest text-muted-foreground",
        className,
      )}
      {...props}
    />
  )
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
